import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, CheckCircle2, Database, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

export function BillsPanel({ props }) {
  const [bills, setBills] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/bills").then((r) => setBills(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/bills/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.auto_booked ? `Auto-booked to ${r.data.suggested.property_name}` : r.data.suggested?.match ? `Matched to ${r.data.suggested.property_name}` : "Uploaded — pick the account to match");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-4xl">
      <MailboxCard onChecked={load} />
      <label className={`ns-card p-6 flex flex-col items-center justify-center gap-2 border-dashed cursor-pointer hover:bg-slate-50 transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="bills-upload-zone">
        <Upload className="w-6 h-6 text-teal-700" />
        <div className="text-sm">{busy ? "Reading bill…" : "Upload a bill (PDF or photo)"}</div>
        <div className="text-[11px] text-slate-500">AI reads the account number, period and amount, then suggests a match</div>
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" data-testid="bills-upload-input"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
      {bills.length === 0 && <div className="text-sm text-slate-400 italic">No bills uploaded yet.</div>}
      {bills.map((b) => <BillRow key={b.id} bill={b} props={props} onChange={load} />)}
    </div>
  );
}

function MailboxCard({ onChecked }) {
  const [boxes, setBoxes] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/bill-mailboxes").then((r) => setBoxes(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    const onMsg = (e) => { if (e.data?.type === "outlook_connected") load(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const connect = async () => {
    try { const r = await api.get("/bill-mailboxes/connect"); window.open(r.data.authorization_url, "billbox", "width=520,height=680"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bill-mailboxes/check");
      const n = r.data.results.reduce((a, x) => a + (x.bills || 0), 0);
      toast.success(`${n} new bill${n === 1 ? "" : "s"} found`);
      load(); onChecked();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const remove = async (email) => { if (window.confirm(`Disconnect ${email}?`)) { await api.delete(`/bill-mailboxes/${encodeURIComponent(email)}`); load(); } };
  return (
    <div className="ns-card p-4" data-testid="bill-mailboxes-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-serif text-lg flex items-center gap-2"><Mail className="w-4 h-4" /> Bill inboxes</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={connect} data-testid="bill-mailboxes-connect">Connect Outlook inbox</Button>
          <Button size="sm" className="rounded-full ns-btn-primary" onClick={check} disabled={busy || !boxes.length} data-testid="bill-mailboxes-check">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Check now
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Sign in as hello@newportstays.co.uk and hello@padmanagr.com. Every hour the CRM looks for bill-like emails with PDF/photo attachments and drops them below for matching.</p>
      {boxes.length === 0 && <div className="text-xs text-slate-400 italic mt-2">No inboxes connected yet.</div>}
      <ul className="mt-2 space-y-1">
        {boxes.map((b) => (
          <li key={b.email} className="flex items-center justify-between text-xs" data-testid={`bill-mailbox-${b.email}`}>
            <span><span className="font-medium">{b.email}</span> · {b.bills_found || 0} bills · checked {b.last_checked_at ? b.last_checked_at.slice(0, 16).replace("T", " ") : "never"}
              {b.last_error && <span className="text-rose-600"> · {b.last_error}</span>}</span>
            <button onClick={() => remove(b.email)} className="text-slate-300 hover:text-red-600 transition-colors" data-testid={`bill-mailbox-remove-${b.email}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BillRow({ bill, props, onChange }) {
  const s = bill.confirmed || bill.suggested || {};
  const ex = bill.extracted || {};
  const [form, setForm] = useState({ property_id: s.property_id || "", account_id: s.account_id || "", month: s.month || "", amount: s.amount ?? ex.amount_due ?? "" });
  const accounts = props.find((p) => p.id === form.property_id)?.utility_accounts || [];
  const confirmed = bill.status === "confirmed";
  const confirm = async () => {
    try {
      await api.post(`/bills/${bill.id}/confirm`, { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Bill booked to the ledger");
      onChange();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async () => { if (window.confirm("Remove this bill?")) { await api.delete(`/bills/${bill.id}`); onChange(); } };
  const open = async () => {
    const r = await api.get(`/bills/${bill.id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-xs";
  return (
    <div className="ns-card p-4" data-testid={`bill-row-${bill.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <button onClick={open} className="flex items-center gap-2 text-sm hover:text-teal-800 transition-colors" data-testid={`bill-open-${bill.id}`}>
          <FileText className="w-4 h-4" /> {bill.filename}
        </button>
        <div className="flex items-center gap-2">
          {confirmed ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`bill-status-${bill.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> {bill.auto_booked ? "Auto-booked" : "Booked"}</span>
            : <span className="text-[11px] text-amber-700" data-testid={`bill-status-${bill.id}`}>{s.match ? `Suggested by ${s.match.replace("_", " ")}` : "Needs matching"}</span>}
          <button onClick={del} className="text-slate-300 hover:text-red-600 transition-colors" data-testid={`bill-delete-${bill.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        {bill.origin?.type === "email" && <span className="text-teal-700">Emailed to {bill.origin.mailbox} · </span>}
        Read from bill: {ex.provider || "—"} · acct {ex.account_number || "—"} · {ex.period_start || "?"} → {ex.period_end || "?"} · £{ex.amount_due ?? "—"}
        {ex.error && <span className="text-rose-600"> · AI could not read this file</span>}
      </div>
      <div className="grid grid-cols-[1.3fr_1.3fr_0.8fr_0.7fr_auto] gap-2 items-center">
        <select className={sel} value={form.property_id} data-testid={`bill-property-${bill.id}`} onChange={(e) => setForm({ ...form, property_id: e.target.value, account_id: "" })}>
          <option value="">Property…</option>
          {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={sel} value={form.account_id} data-testid={`bill-account-${bill.id}`} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
          <option value="">Utility account…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{`${a.kind} · ${a.provider || ""} ${a.account_number || ""}`}</option>)}
        </select>
        <Input type="month" className="h-9 text-xs" value={form.month} data-testid={`bill-month-${bill.id}`} onChange={(e) => setForm({ ...form, month: e.target.value })} />
        <Input type="number" step="0.01" className="h-9 text-xs" value={form.amount} data-testid={`bill-amount-${bill.id}`} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <Button size="sm" className="ns-btn-primary rounded-full" data-testid={`bill-confirm-${bill.id}`}
          disabled={!form.property_id || !form.account_id || !form.month || !form.amount} onClick={confirm}>
          {confirmed ? "Update" : "Confirm"}
        </Button>
      </div>
    </div>
  );
}

export function PodSyncCard() {
  const [status, setStatus] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState("");
  const check = async () => {
    setBusy("check");
    try { const r = await api.get("/pod-secure/status"); setStatus(r.data); }
    catch (err) { setStatus({ reachable: false, error: apiError(err) }); }
    finally { setBusy(""); }
  };
  const sync = async () => {
    setBusy("sync");
    try { const r = await api.post("/pod-secure/sync"); setRun(r.data); toast.success(`${r.data.meter_readings} readings · ${r.data.files} files pulled`); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };
  return (
    <div className="ns-card p-4" data-testid="pod-sync-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-serif text-lg flex items-center gap-2"><Database className="w-4 h-4" /> Pod-secure database</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={check} disabled={!!busy} data-testid="pod-sync-check">{busy === "check" ? "Checking…" : "Check connection"}</Button>
          <Button size="sm" className="rounded-full ns-btn-primary" onClick={sync} disabled={!!busy} data-testid="pod-sync-run">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy === "sync" ? "animate-spin" : ""}`} /> Sync now
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Pulls meter readings and uploaded bill records straight from pod-secure. Safe to re-run — already-synced rows are skipped.</p>
      {status && !status.reachable && <p className="text-xs text-rose-700 mt-2" data-testid="pod-sync-error">{status.error}</p>}
      {status?.reachable && (
        <ul className="text-xs mt-2 space-y-0.5" data-testid="pod-sync-collections">
          {status.collections.map((c) => <li key={c.name}><span className="font-mono">{c.name}</span> · {c.count} rows {c.bucket && <span className="text-teal-700">→ {c.bucket.replace("_", " ")}</span>}</li>)}
        </ul>
      )}
      {run && <p className="text-xs text-slate-600 mt-2" data-testid="pod-sync-result">{run.meter_readings} new readings · {run.files} new files · {run.skipped_existing} already synced · {run.unmapped_property} not linked to a property</p>}
    </div>
  );
}
